import {Component, inject, OnDestroy, OnInit, signal, input, output} from '@angular/core';
import {ControlValueAccessor, FormsModule, NgControl} from '@angular/forms';
import {DirectusService, PaymentPlan} from '@frontend/directus';
import {firstValueFrom, lastValueFrom, Subscription} from 'rxjs';
import {Select} from 'primeng/select';
import {TranslocoService} from '@jsverse/transloco';
import {NgClass} from '@angular/common';

export interface PaymentPlanOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-payment-plan-select',
  imports: [FormsModule, Select, NgClass],
  templateUrl: './payment-plan-select.component.html',
  styleUrl: './payment-plan-select.component.scss',
})
export class PaymentPlanSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  readonly placeholder = input<string>();
  readonly showClear = input(false);
  readonly appendTo = input<string>();
  readonly preloadedPlans = input<PaymentPlan[]>();
  readonly showMonthLength = input(true);
  readonly includeRetiredPlans = input(false);
  readonly plansLoaded = output<PaymentPlan[]>();

  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  public readonly ngControl = inject(NgControl, {optional: true, self: true});

  readonly options = signal<PaymentPlanOption[]>([]);
  private paymentPlans: PaymentPlan[] = [];
  private langSub: Subscription | undefined;

  value: string | null = null;
  disabled = false;

  private onChange!: (value: string | null) => void;
  private onTouched!: () => void;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    const preloadedPlans = this.preloadedPlans();
    if (preloadedPlans && preloadedPlans.length > 0) {
      this.paymentPlans = this.visiblePlans(preloadedPlans);
      this.updateOptions();
      this.plansLoaded.emit(this.paymentPlans);
    } else {
      this.loadPaymentPlans();
    }

    this.langSub = this.translocoService.langChanges$.subscribe(async (lang) => {
      await lastValueFrom(this.translocoService.load(lang));
      this.updateOptions();
    });
  }

  private async loadPaymentPlans(): Promise<void> {
    const plans = await firstValueFrom(this.directusService.loadPaymentPlans());
    this.paymentPlans = this.visiblePlans(plans);
    this.updateOptions();
    this.plansLoaded.emit(this.paymentPlans);
  }

  private visiblePlans(plans: PaymentPlan[]): PaymentPlan[] {
    return plans.filter(
      (plan) => this.includeRetiredPlans() || plan.availableForRegistration !== false,
    );
  }

  private updateOptions(): void {
    const currentLang = this.translocoService.getActiveLang();
    const monthsText = this.translocoService.translate('common.months');
    const newOptions = this.paymentPlans.map((plan) => {
      const translation = plan.translations?.find((t) => t.language === currentLang);
      const name = translation?.name ?? 'Unknown Plan';
      const duration = plan.isMinimumRuntime
        ? this.translocoService.translate('account.register.minimumPlanRuntime', {
            months: plan.monthRuntime,
          })
        : `${plan.monthRuntime} ${monthsText}`;
      const label = this.showMonthLength() ? `${name} (${duration})` : name;
      return {
        label,
        value: plan.id,
      };
    });
    this.options.set(newOptions);
  }

  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }

  onValueChange(value: string | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
